import React, { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import Flag from 'react-country-flag'; // npm install react-country-flag
 
/* -------------------- ENHANCED CONTACT FORM WITH COUNTRY DROPDOWN ----------------------- */
const API_URL = 'https://api.securxperts.com:8000';
const ENDPOINT = '/forms/sams';
 
const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country_code: "",
    phoneno: "",
    location: "",
    no_of_branches: "",
    industry_type: "",
    no_of_people: "",
  });
 
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const dropdownRef = useRef(null);
 
  // Input filters
  const nameFilter = useCallback((value) => /^[A-Za-z\s]*$/.test(value) ? value.slice(0, 60) : form.name, [form.name]);
  const phoneFilter = useCallback((value) => /^\d*$/.test(value) ? value.slice(0, 10) : form.phoneno, [form.phoneno]);
  const locationFilter = useCallback((value) => value.slice(0, 60), []);
  const branchesFilter = useCallback((value) => /^\d*$/.test(value) ? value.slice(0, 10) : form.no_of_branches, [form.no_of_branches]);
  const typeFilter = useCallback((value) => value.slice(0, 60), []);
  const peopleFilter = useCallback((value) => /^\d*$/.test(value) ? value.slice(0, 10) : form.no_of_people, [form.no_of_people]);
 
  // COUNTRIES WITH FLAGS (you can expand this list)
  const countries = [
    { code: '+91', iso: 'IN', name: 'India' },
    { code: '+1', iso: 'US', name: 'United States' },
    { code: '+44', iso: 'GB', name: 'United Kingdom' },
    { code: '+971', iso: 'AE', name: 'United Arab Emirates' },
    { code: '+61', iso: 'AU', name: 'Australia' },
    { code: '+852', iso: 'HK', name: 'Hong Kong' },
    { code: '+36', iso: 'HU', name: 'Hungary' },
    { code: '+354', iso: 'IS', name: 'Iceland' },
    { code: '+93', iso: 'AF', name: 'Afghanistan' },
    { code: '+355', iso: 'AL', name: 'Albania' },
  ];
 
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.toLowerCase().includes(countrySearch.toLowerCase())
  );
 
  const navigate = useNavigate();
 
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (!/^[A-Za-z\s]+$/.test(value)) return 'Only letters and spaces allowed';
        if (value.length < 2) return 'Name must be at least 2 characters';
        return '';
 
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter valid email format';
        return '';
 
      case 'country_code':
        if (!value.trim()) return 'Country code is required';
        if (!countries.some(c => c.code === value)) return 'Select valid country code from dropdown';
        return '';
 
      case 'phoneno':
        if (!value.trim()) return 'Phone number is required';
        if (!/^\d+$/.test(value)) return 'Only digits allowed';
        if (value.length !== 10) return 'Phone must be exactly 10 digits';
        return '';
 
      case 'location':
        if (!value.trim()) return 'School location is required';
        return '';
 
      case 'no_of_branches':
        if (!value.trim()) return 'Number of branches required';
        const branches = parseInt(value);
        if (isNaN(branches) || branches < 0) return 'Enter valid number (0 or more)';
        return '';
 
      case 'industry_type':
        if (!value.trim()) return 'Type required (School/College)';
        return '';
 
      case 'no_of_people':
        if (!value.trim()) return 'Number of users required';
        const people = parseInt(value);
        if (isNaN(people) || people < 1) return 'Enter valid number (minimum 1)';
        return '';
 
      default:
        return '';
    }
  };
 
  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;
 
    switch (name) {
      case 'name': filteredValue = nameFilter(value); break;
      case 'phoneno': filteredValue = phoneFilter(value); break;
      case 'location': filteredValue = locationFilter(value); break;
      case 'no_of_branches': filteredValue = branchesFilter(value); break;
      case 'industry_type': filteredValue = typeFilter(value); break;
      case 'no_of_people': filteredValue = peopleFilter(value); break;
    }
 
    setForm(prev => ({ ...prev, [name]: filteredValue }));
    const errorMsg = validateField(name, filteredValue);
    setErrors(prev => ({ ...prev, [name]: errorMsg }));
  };
 
  const selectCountry = (code) => {
    setForm(prev => ({ ...prev, country_code: code }));
    setIsCountryOpen(false);
    setCountrySearch('');
    setErrors(prev => ({ ...prev, country_code: '' }));
  };
 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCountryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  const validateAll = () => {
    const newErrors = {};
    Object.keys(form).forEach(key => {
      newErrors[key] = validateField(key, form[key]);
    });
    setErrors(newErrors);
    return Object.values(newErrors).every(err => err === '');
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;
 
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}${ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
 
      if (resp.ok) {
        setShowSuccessPopup(true);
        setForm({
          name: "", email: "", country_code: "", phoneno: "",
          location: "", no_of_branches: "", industry_type: "", no_of_people: "",
        });
        setErrors({});
      } else {
        console.error('Submit failed:', resp.status);
      }
    } catch (error) {
      console.error('Network error:', error);
    } finally {
      setSubmitting(false);
    }
  };
 
  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => setShowSuccessPopup(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);
 
  if (showSuccessPopup) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Helmet>
          <title>School Management Software | AI-Powered School ERP | SecurXpert</title>
          <meta name="description" content="AI-enabled school management software by SecurXpert to automate academics, administration, attendance, fees and communication." />
        </Helmet>
        <div className="bg-white rounded-xl max-w-xs w-full shadow-2xl relative p-4 animate-in fade-in zoom-in duration-200">
          <button
            onClick={() => setShowSuccessPopup(false)}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold p-1 hover:bg-gray-100 rounded-full transition-all w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
          <div className="text-center pt-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-green-600 mb-1">Success</h3>
            <p className="text-gray-600 text-sm">Your request has been Submitted Successfully.</p>
          </div>
        </div>
      </div>
    );
  }
 
  const hasAnyError = Object.values(errors).some(e => e);
 
  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white p-6 md:p-10 rounded-2xl shadow-xl border border-gray-200">
      <h3 className="text-3xl font-bold text-center mb-8 text-[#0a66c2]">
        School ERP Enquiry Form
      </h3>
 
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          { name: 'name', placeholder: 'Full Name *', span: 1, maxLength: 60 },
          { name: 'email', type: 'email', placeholder: 'Email *', span: 1, maxLength: 60 },
          { name: 'country_code', placeholder: 'Country Code *', span: 1, isDropdown: true },
          { name: 'phoneno', placeholder: 'Phone (10 digits) *', span: 1 },
          { name: 'location', placeholder: 'School Location *', span: 1, maxLength: 60 },
          { name: 'no_of_branches', type: 'number', placeholder: 'No. of Branches *', span: 1, maxLength: 10 },
          { name: 'industry_type', placeholder: 'Type (School/College) *', span: 1, maxLength: 60 },
          { name: 'no_of_people', type: 'number', placeholder: 'No. of Users *', span: 1, maxLength: 10 },
        ].map((field) => (
          <div key={field.name} className={field.span === 2 ? 'md:col-span-2' : ''}>
            {field.isDropdown ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsCountryOpen(!isCountryOpen)}
                  className={`w-full px-4 py-3 rounded-xl border-2 border-gray-300 bg-white text-gray-800 placeholder-gray-400 text-left flex justify-between items-center hover:border-gray-400 focus:outline-none focus:border-[#0a66c2] focus:ring-1 focus:ring-[#0a66c2]/30 transition-all pr-10 ${
                    errors.country_code ? 'border-orange-400 ring-2 ring-orange-200' : ''
                  }`}
                >
                  <span className="flex items-center gap-2 truncate flex-1">
                    {form.country_code ? (
                      <>
                        <Flag
                          countryCode={countries.find(c => c.code === form.country_code)?.iso}
                          svg className="w-5 h-3.5 flex-shrink-0 rounded"
                        />
                        <span className="font-medium text-sm truncate">
                          {countries.find(c => c.code === form.country_code)?.name}
                        </span>
                        <span className="text-xs font-mono text-gray-500 ml-1">
                          {form.country_code}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-500 text-sm">Select Country Code</span>
                    )}
                  </span>
                  <svg className={`w-5 h-5 transition-transform text-gray-500 ${isCountryOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {errors.country_code && (
                  <p className="mt-1 text-xs text-orange-400 font-medium pl-2">{errors.country_code}</p>
                )}
 
                {isCountryOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-hidden">
                    <input
                      type="text"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      placeholder="Search country or code..."
                      className="w-full px-4 py-3 border-b border-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-[#0a66c2]"
                      autoFocus
                    />
                    <div className="max-h-64 overflow-auto">
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => selectCountry(country.code)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-all flex items-center gap-3 text-sm first:pt-2 last:pb-2 focus:outline-none focus:bg-gray-100"
                          >
                            <Flag countryCode={country.iso} svg className="w-6 h-4 flex-shrink-0 rounded" />
                            <span className="font-medium flex-1 min-w-0 truncate pr-4">{country.name}</span>
                            <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                              {country.code}
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">No countries found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <input
                  name={field.name}
                  type={field.type || 'text'}
                  value={form[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  maxLength={field.maxLength}
                  required
                  className={`w-full px-4 py-3 rounded-xl border-2 border-gray-300 bg-white
                    text-gray-800 placeholder-gray-400
                    focus:outline-none focus:border-[#0a66c2]
                    focus:ring-1 focus:ring-[#0a66c2]/30
                    transition-all duration-200
                    ${errors[field.name] ? 'border-orange-400 ring-2 ring-orange-200' : ''}`}
                />
                {errors[field.name] && (
                  <p className="mt-1 text-xs text-orange-400 font-medium pl-2">{errors[field.name]}</p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
 
      <button
        type="submit"
        disabled={submitting || hasAnyError}
        className="w-full bg-[#0a66c2] hover:bg-[#084c93] disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold mt-8 shadow-md transition-all duration-300 text-lg"
      >
        {submitting ? "Submitting..." : "Submit Enquiry"}
      </button>
    </form>
  );
};
 
export default function Vishan() {
  const location = useLocation(); // ← Added for route-based scroll reset
 
  const SCROLL_SHOW_THRESHOLD = 300;
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
 
  const scrollTimeoutRef = useRef(null);
  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;

  // Force scroll to top on mount AND when route changes (including refresh)
  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top on component mount with multiple attempts
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (window.scrollY !== 0) {
        // If still not at top, try again after a short delay
        setTimeout(forceScrollToTop, 50);
      }
    };
    
    forceScrollToTop();
    
    // Additional fallback for stubborn browsers
    setTimeout(forceScrollToTop, 100);
    setTimeout(forceScrollToTop, 300);
    
    return () => {
      // Restore scroll restoration when component unmounts
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, [location.pathname]); // Re-run when path changes (including refresh on same page)

  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);
    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
 
    setScrollProgress(Math.min(100, Math.max(0, progress)));
    setIsScrolling(true);
 
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
 
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);
 
  useEffect(() => {
    window.scrollTo(0, 0); // initial safety
    onScroll();
 
    window.addEventListener("scroll", onScroll, { passive: true });
 
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [onScroll]);
 
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
 
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };
 
  return (
    <div className="w-full font-sans text-[126, 202, 242] overflow-hidden">
      <Navbar />
 
      {/* ================= HERO SECTION ================= */}
      <section className="relative w-full bg-[#9999] text-white px-6 md:px-16 py-3">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <img src="/img/62.png" alt="Logo" className="w-24 mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              School Management Software for Modern Campuses
            </h1>
            <p className="mt-4 text-lg text-white/90">
              SecurXpert’s AI-enabled School Management Software is a powerful, cloud-based ERP designed to simplify academic operations, strengthen communication and enhance campus-wide administration. Manage every school function efficiently and securely from student records to staff coordination on one intelligent digital platform.
            </p>
          </div>
          <div className="flex justify-center">
            <img
              src="/img/samsheroimg.png"
              alt="AI powered school management software dashboard"
              className="w-100 md:w-96 rounded-xl mt-20"
            />
          </div>
        </div>
      </section>
 
      {/* ================= VIDEO SECTION ================= */}
      <section className="w-full py-20 px-6 md:px-0 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-10">Explore School Management Live</h2>
          <p>See how our software makes school operations simple and efficient—handling everything from student records to staff co-ordination on one smart, digital platform.</p>
          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
  <video
    className="w-full h-auto max-h-[240px] sm:max-h-[360px] md:max-h-none block"
    src="/video/vishan.mp4"
    muted
    playsInline
    autoPlay
    loop
    preload="auto"
    controls
  >
    Your browser does not support HTML5 video.
  </video>
</div>

        </div>
      </section>
 
      {/* ================= FEATURES SECTION ================= */}
      <section className="w-full px-6 md:px-16 py-20 bg-[#f7faff]">
  <div className="max-w-6xl mx-auto text-center">
    <h2 className="text-3xl font-bold mb-10">
      Why Schools Prefer Our Management Software
    </h2>
    <div className="grid md:grid-cols-4 gap-6">
      <div className="bg-white p-6 rounded-xl shadow transition-all duration-300 hover:bg-[#0a66c2] cursor-pointer hover:shadow-lg">
        <div className="flex justify-center mb-4">
          <img src="/img/fullyautomated.png" alt="Fully automated school operations" className="w-14 h-14" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Fully Automated School Operations</h3>
        <p className="text-gray-600 text-sm">Automate academic, administrative, and financial tasks to boost productivity</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow transition-all duration-300 hover:bg-[#0a66c2] cursor-pointer hover:shadow-lg">
        <div className="flex justify-center mb-4">
          <img src="/img/userfriendly.png" alt="User friendly interface" className="w-14 h-14" />
        </div>
        <h3 className="text-xl font-semibold mb-2">User-Friendly & Intuitive Design</h3>
        <p className="text-gray-600 text-sm">Clean, structured interface ensures easy navigation</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow transition-all duration-300 hover:bg-[#0a66c2] cursor-pointer hover:shadow-lg">
        <div className="flex justify-center mb-4">
          <img src="/img/accessible.png" alt="Accessible on any device" className="w-14 h-14" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Accessible on Any Device</h3>
        <p className="text-gray-600 text-sm">Track attendance, homework, fees anytime</p>
      </div>
      <div className="bg-white p-6 rounded-xl shadow transition-all duration-300 hover:bg-[#0a66c2] cursor-pointer hover:shadow-lg">
        <div className="flex justify-center mb-4">
          <img src="/img/fitsschools.png" alt="Scalable for all sizes" className="w-14 h-14" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Fits Schools of Every Size</h3>
        <p className="text-gray-600 text-sm">Adapts to your school's structure and growth</p>
      </div>
    </div>
  </div>
</section>

 
      {/* ================= APP SECTION ================= */}
      <section className="w-full px-6 md:px-16 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold">Core Capabilities of Our School Management Software</h2>
          <div className="grid md:grid-cols-2 gap-10 mt-12">
            <img src="/img/sams1.png" alt="Streamlined workflows" className="rounded-xl shadow-lg" />
            <div className="grid gap-6">
              <div className="p-6 bg-[#f7faff] rounded-xl shadow">
                <h3 className="text-xl font-semibold mb-2">Streamlined Academic & Administrative Workflows</h3>
              </div>
              <div className="p-6 bg-[#f7faff] rounded-xl shadow">
                <h3 className="text-xl font-semibold mb-2">Complete Student Lifecycle Management</h3>
              </div>
              <div className="p-6 bg-[#f7faff] rounded-xl shadow">
                <h3 className="text-xl font-semibold mb-2">All Essential Modules in One System</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
 
      {/* ================= GET IN TOUCH SECTION ================= */}
      <section className="w-full px-6 md:px-16 py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Get in Touch</h2>
          <p className="text-center text-gray-600 mb-10">
            Fill out the form below and our School ERP experts will contact you.
          </p>
          <ContactForm />
        </div>
      </section>
 
      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
 
          <svg className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
 
      <Footer />
    </div>
  );
}
 